import { PrintDocument } from '@/components/ui/PrintDocument';
import { env } from '@/config/env';
import { useInstituteBranding } from '@/features/institute/store/branding.store';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { ExportRows } from '@/types/export';

/** A register ready for paper: the rows, what heads the sheet, and the file's name. */
export interface PrintableTable extends ExportRows {
  /** Heads the paper — "Fee register 2026-27". */
  title: string;
  /** Without an extension; what "Save as PDF" names the file. */
  fileName: string;
}

/**
 * Saving a register as a PDF: printed landscape, headed with the school's own
 * name, through the browser's "Save as PDF" like every other paper here.
 *
 * Shared by the pick-your-columns `ExportDialog` and the registers that export
 * a fixed set of columns (fees), so a school's exports all come out on the same
 * sheet. Prints while `table` is set; `onFinished` is where the caller clears it.
 */
export function ExportTablePrint({
  table,
  onFinished,
}: {
  table: PrintableTable | null;
  onFinished: () => void;
}) {
  const branding = useInstituteBranding((state) => state.branding);

  return (
    <PrintDocument
      isOpen={table !== null}
      onFinished={onFinished}
      fileName={table?.fileName ?? ''}
      landscape
    >
      {table && (
        <ExportTableDocument
          title={table.title}
          table={table}
          // The paper must never go out headed with another institute's name.
          instituteName={branding?.displayName ?? env.appName}
        />
      )}
    </PrintDocument>
  );
}

/**
 * The register on paper. Plain like the fee receipt — black on white, no
 * colour that costs a school ink — and landscape, because a register is wider
 * than it is tall. The header row repeats on every sheet.
 *
 * A `div` rather than a `<header>`: the print rules hide every `<header>` on
 * the page so the app's own top bar never reaches the paper.
 */
function ExportTableDocument({
  title,
  table,
  instituteName,
}: {
  title: string;
  table: ExportRows;
  instituteName: string;
}) {
  const colCount = table.header.length;

  // Scale down typography and padding dynamically when many columns are exported
  // so no column clips or overflows the page boundaries.
  const textSizeClass =
    colCount <= 6
      ? 'text-xs'
      : colCount <= 9
        ? 'text-[11px] leading-snug'
        : colCount <= 12
          ? 'text-[10px] leading-tight'
          : colCount <= 15
            ? 'text-[8.5px] leading-tight'
            : 'text-[7.5px] leading-tight';

  const cellPaddingClass =
    colCount <= 6
      ? 'px-2 py-1.5'
      : colCount <= 9
        ? 'px-1.5 py-1'
        : colCount <= 12
          ? 'px-1 py-0.5'
          : 'px-0.5 py-0.5';

  return (
    <article className="print-landscape bg-white text-black w-full max-w-full">
      <div className="border-b-2 border-black pb-2 text-center">
        <h1 className="text-lg font-bold">{instituteName}</h1>
        <p className="mt-0.5 text-xs font-semibold uppercase tracking-widest">{title}</p>
        <p className="mt-0.5 text-[11px] text-gray-700">
          {table.rows.length.toLocaleString('en-IN')} records · Generated on{' '}
          {formatDateTime(new Date())}
        </p>
      </div>

      <table
        className={cn(
          'export-document-table mt-3 w-full border-collapse border border-black',
          textSizeClass,
        )}
        style={{ tableLayout: 'auto', width: '100%', maxWidth: '100%' }}
      >
        <thead>
          <tr className="bg-slate-100">
            {table.header.map((heading) => (
              <th
                key={heading}
                scope="col"
                className={cn(
                  'border border-black text-left align-bottom font-bold text-black',
                  cellPaddingClass,
                  'break-words [overflow-wrap:anywhere] [word-break:break-word]',
                )}
                style={{ overflowWrap: 'anywhere', wordBreak: 'break-word', whiteSpace: 'normal' }}
              >
                {heading}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, rowIndex) => (
            <tr key={rowIndex} className="break-inside-avoid">
              {row.map((value, column) => (
                <td
                  key={column}
                  className={cn(
                    'border border-black align-top text-black',
                    cellPaddingClass,
                    'break-words [overflow-wrap:anywhere] [word-break:break-word]',
                  )}
                  style={{ overflowWrap: 'anywhere', wordBreak: 'break-word', whiteSpace: 'normal' }}
                >
                  {value ?? ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {table.skipped > 0 && (
        <p className="mt-2 text-xs font-semibold">
          {table.skipped.toLocaleString('en-IN')} more records matched but could not fit in one
          file.
        </p>
      )}

      <p className="mt-4 text-center text-xs text-gray-700">Confidential — for the school office only.</p>
    </article>
  );
}
